import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatPkgAddresses,
  formatPkgDate,
  PKG
} from '../../utils/package-fields';

/** Columns that support server-side sorting with their API field names */
const SORTABLE_COLUMNS = {
  ID: 'id',
  'Direction Type': 'direction_type',
  Company: 'company_name',
  'Tracking Number': 'tracking_number',
  'Batch Name': 'batch_name',
  'Date Collected': 'createdAt'
};

const ALL_COLUMNS = [
  'ID',
  'Direction Type',
  'Company',
  'Tracking Number',
  'Batch Name',
  'Date Collected',
  'Address',
  'Recipient',
  'Zoe Status',
  'Courier Status',
  'Action'
];

export default function PackageTable({
  packages,
  sortBy = 'default',
  sortOrder = 'default',
  onSort
}) {
  const navigate = useNavigate();

  const getLatestTrackingStatus = (trackingRecord) => {
    if (
      !trackingRecord ||
      !trackingRecord.tracking_history ||
      trackingRecord.tracking_history.length === 0
    ) {
      return 'N/A';
    }

    const sortedHistory = [...trackingRecord.tracking_history].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );

    return sortedHistory[0]?.status || 'N/A';
  };

  // Colour for the Zoe purchase-order Open/Closed chip (matches PurchaseOrdersPage).
  const getZoePoStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'open':
        return 'primary';
      case 'closed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.100' }}>
            {ALL_COLUMNS.map((header) => {
              const sortField = SORTABLE_COLUMNS[header];
              const isSortable = Boolean(sortField) && Boolean(onSort);
              const isActive = sortBy === sortField;
              const direction =
                isActive && sortOrder !== 'default' ? sortOrder : 'asc';

              return (
                <TableCell
                  key={header}
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 1 }}
                  sortDirection={isActive ? direction : false}
                >
                  {isSortable ? (
                    <Box display="flex" alignItems="center" gap={0.25}>
                      <span>{header}</span>
                      <IconButton
                        size="small"
                        onClick={() => onSort(sortField, 'asc')}
                        sx={{
                          p: 0.25,
                          color:
                            isActive && sortOrder === 'asc'
                              ? 'primary.main'
                              : 'text.disabled',
                          opacity: isActive && sortOrder === 'asc' ? 1 : 0.45,
                          '&:hover': { opacity: 1 }
                        }}
                        aria-label={`Sort ${header} ascending`}
                      >
                        <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onSort(sortField, 'desc')}
                        sx={{
                          p: 0.25,
                          color:
                            isActive && sortOrder === 'desc'
                              ? 'primary.main'
                              : 'text.disabled',
                          opacity: isActive && sortOrder === 'desc' ? 1 : 0.45,
                          '&:hover': { opacity: 1 }
                        }}
                        aria-label={`Sort ${header} descending`}
                      >
                        <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    header
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {packages.length > 0 ? (
            packages.map((pkg) => (
              <TableRow
                key={pkg.id}
                hover
                onClick={() => navigate(`/package-details/${pkg.id}`)}
                sx={{ cursor: 'pointer', '&:last-child td': { border: 0 } }}
              >
                <TableCell>{pkg.id}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {pkg.direction_type}
                </TableCell>
                <TableCell>{pkg.company_name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {pkg.tracking_number}
                    {pkg[PKG.IS_DUPLICATE] && (
                      <Chip
                        label="Duplicate"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{pkg[PKG.BATCH_NAME] || 'N/A'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {formatPkgDate(pkg)}
                </TableCell>
                <TableCell>
                  {formatPkgAddresses(pkg).map((addr, i) => (
                    <div key={i}>{addr}</div>
                  ))}
                </TableCell>
                {/* Recipient = who scanned the package (its scanning user).
                    The OCR label addressee still lives in recipient_name and is
                    shown on the package details page. */}
                <TableCell>{pkg.user?.username || 'N/A'}</TableCell>
                {/* Zoe Status = the linked Zoe purchase order's Open/Closed status
                    (PurchaseOrders row whose POTitle contains this tracking number). */}
                <TableCell>
                  {pkg.zoe_po_status ? (
                    <Chip
                      label={pkg.zoe_po_status}
                      size="small"
                      color={getZoePoStatusColor(pkg.zoe_po_status)}
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  ) : (
                    'N/A'
                  )}
                </TableCell>
                <TableCell>
                  {getLatestTrackingStatus(pkg?.trackingRecord)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/package-details/${pkg.id}`);
                    }}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={ALL_COLUMNS.length} align="center">
                No packages found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
